import React, { useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Button,
    CircularProgress,
    Stack,
    Divider,
    Alert,
    Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ThemeContext } from 'styled-components';
import ApiClient from "../../../services/ApiClient";
import { CsrfContext } from "../../../providers/CsrfProvider";
import { AuthContext } from "../../auth/providers/AuthProvider";
import { addNewTransaction, verifyTransaction } from "../../../services/template/Transactions";
import NewUUID from "../../../utils/randy/NewUUID";
import FormatDate from "../../../utils/formatters/FormatDate";
import ButtonOutlined from "../../common/components/Buttons/ButtonOutlined";
import InputWithActions from "../../common/components/Inputs/InputWithActions";
import InputValidatorField from "../../common/components/Inputs/InputValidatorField";
import {IsValidText} from "../../../utils/Validators";
import ButtonPrimary from "../../common/components/Buttons/ButtonPrimary";
import DynamicActionButton from "../../common/components/Buttons/DynamicActionButton";
import SectionHeader from "../components/SectionHeader";

const PAYMENT_NOTE = "ezsplit";
const VENMO_PROVIDER = "venmo";

const fetchPaymentOptions = async (csrfToken, templateId) => {
    const { data } = await ApiClient.get(`/api/banking/templates/${templateId}`, {
        headers: {
            accept: "text/json",
            request_id: NewUUID(),
            "X-CSRF-Token": csrfToken
        }
    });
    return data.response;
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(Math.abs(amount));
};

const buildVenmoUrl = (venmoId, amount) => {
    const cleanedVenmoId = venmoId.replace(/^@/, '');
    const encodedAmount = encodeURIComponent(Math.abs(amount));
    const encodedNote = encodeURIComponent(PAYMENT_NOTE);
    return `https://venmo.com/?txn=pay&recipients=${cleanedVenmoId}&amount=${encodedAmount}&note=${encodedNote}`;
};

const TransactionModal = ({ open, handleClose, item, isOwner, templateId, statements, transactions }) => {
    const { csrfToken } = useContext(CsrfContext);
    const { authClaims } = useContext(AuthContext);
    const theme = useContext(ThemeContext);
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({ amount: 0 });
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [venmoUrl, setVenmoUrl] = useState(null);
    const [amountError, setAmountError] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [selectedStatementId, setSelectedStatementId] = useState(null);
    const [detailsExpanded, setDetailsExpanded] = useState(false);
    const [confirmPaymentAmount, setConfirmPaymentAmount] = useState(null);

    // Check if this is a participant viewing their own payment transaction
    const isParticipantViewingOwnPayment = !isOwner && item && item.amount > 0 && item.user?.sub && authClaims?.user_sub && item.user.sub.toUpperCase() === authClaims.user_sub.toUpperCase();

    // Calculate unpaid balance for each statement for the current user
    const statementsWithBalance = React.useMemo(() => {
        if (!statements || !transactions || !item?.user?.sub) {
            return [];
        }

        return statements.map(statement => {
            // Find all transactions for this statement and this user
            const userTransactions = (transactions || []).filter(t =>
                t.statement_id === statement.id &&
                t.user?.sub &&
                t.user.sub.toUpperCase() === item.user.sub.toUpperCase()
            );

            // Calculate total paid (positive transactions that are verified)
            const totalPaid = userTransactions
                .filter(t => t.amount > 0 && t.verified_at)
                .reduce((sum, t) => sum + t.amount, 0);

            // Calculate total owed (negative transactions)
            const totalOwed = userTransactions
                .filter(t => t.amount < 0)
                .reduce((sum, t) => sum + Math.abs(t.amount), 0);

            const unpaidBalance = totalOwed - totalPaid;

            return {
                ...statement,
                unpaidBalance,
                totalOwed,
                totalPaid
            };
        }).filter(s => s.unpaidBalance > 0) // Only show statements with unpaid balance
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // Sort by oldest first
    }, [statements, transactions, item?.user?.sub]);

    useEffect(() => {
        if (open) {
            setFormData({ amount: 0 });
            setConfirmDialogOpen(false);
            setVenmoUrl(null);
            setConfirmPaymentAmount(null);
            setAmountError(false);
            setSubmitAttempted(false);
            setDetailsExpanded(false);
            // Default to oldest statement if available
            if (statementsWithBalance && statementsWithBalance.length > 0) {
                setSelectedStatementId(statementsWithBalance[0].id);
            } else {
                setSelectedStatementId(null);
            }
        }
    }, [open, statementsWithBalance]);

    const { data: paymentOptions, isLoading, isError } = useQuery(
        ['template_payment_options', item.bill_id],
        () => fetchPaymentOptions(csrfToken, item.bill_id),
        {
            enabled: open && !isOwner && !isParticipantViewingOwnPayment
        }
    );

    const mutation = useMutation(addNewTransaction(csrfToken), {
        onSuccess: () => {
            queryClient.invalidateQueries(['transactions', String(templateId)]);
            queryClient.invalidateQueries(['templates']); // Refresh bill list with updated balances
            setFormData({ amount: 0 });
            handleClose();
        },
    });

    const confirmPaymentMutation = useMutation(addNewTransaction(csrfToken), {
        onSuccess: () => {
            queryClient.invalidateQueries(['transactions', String(templateId)]);
            queryClient.invalidateQueries(['templates']); // Refresh bill list with updated balances
            setConfirmDialogOpen(false);
            setVenmoUrl(null);
            setConfirmPaymentAmount(null);
        },
    });

    const verifyTransactionMutation = useMutation(verifyTransaction(csrfToken), {
        onSuccess: () => {
            queryClient.invalidateQueries(['transactions', String(templateId)]);
            queryClient.invalidateQueries(['templates']); // Refresh bill list with updated balances
            handleClose();
        },
    });

    const handleVerifyTransaction = (verified) => {
        // Verify or deny transaction - owner confirming or denying that a user made a payment
        if (item?.id && item?.statement_id && templateId) {
            verifyTransactionMutation.mutate({
                statementId: item.statement_id,
                transactionId: item.id,
                templateId: templateId,
                verified: verified
            });
        }
    };

    const handleVenmoClick = (url) => {
        // Open Venmo immediately in new window
        window.open(url, '_blank', 'noopener,noreferrer');

        // Store the URL and show confirmation dialog
        setVenmoUrl(url);
        setConfirmDialogOpen(true);
    };

    const handleConfirmPayment = () => {
        // Use entered amount (Pay Bill) or total balance; apply to selected/oldest statement
        const paymentAmount = confirmPaymentAmount ?? Math.abs(item.amount);
        const statementIdToUse = selectedStatementId ?? statementsWithBalance?.[0]?.id ?? item.statement_id;

        if (templateId && statementIdToUse && item.user?.sub) {
            confirmPaymentMutation.mutate({
                amount: paymentAmount,
                user_sub: item.user.sub,
                statement_id: statementIdToUse,
                templateId: templateId
            });
        }

        setConfirmDialogOpen(false);
        setVenmoUrl(null);
        setConfirmPaymentAmount(null);
    };

    const handleCancelPayment = () => {
        setConfirmDialogOpen(false);
        setVenmoUrl(null);
        setConfirmPaymentAmount(null);
    };

    const handleLaterPayment = () => {
        setConfirmDialogOpen(false);
        setVenmoUrl(null);
        setConfirmPaymentAmount(null);
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        let parsedValue = value;
        if (e.target.type === 'number') {
            parsedValue = parseFloat(value);
            if (isNaN(parsedValue)) {
                parsedValue = 0;
            }
        }
        setFormData((prevData) => ({
            ...prevData,
            [name]: parsedValue,
        }));
        // Clear error when user starts typing
        if (submitAttempted && amountError) {
            const num = parseFloat(value);
            if (!isNaN(num) && num > 0) {
                setAmountError(false);
            }
        }
    };

    const validateAmount = (amount) => {
        const num = parseFloat(amount);
        return !isNaN(num) && num > 0;
    };

    const onSubmit = () => {
        setSubmitAttempted(true);
        const isValid = validateAmount(formData.amount);

        if (!isValid) {
            setAmountError(true);
            return;
        }

        // If no statement is selected, use the oldest unpaid statement (default behavior)
        const statementIdToUse = selectedStatementId ||
            (statementsWithBalance && statementsWithBalance.length > 0 ? statementsWithBalance[0].id : item.statement_id);

        setAmountError(false);
        const transactionData = {
            ...formData,
            user_sub: item.user.sub,
            statement_id: statementIdToUse,
            templateId: templateId
        };
        mutation.mutate(transactionData);
    };

    const renderVerificationStatus = () => {
        const isVerified = item.verified_at !== null && item.verified_at !== undefined;
        const isDenied = item.denied_at !== null && item.denied_at !== undefined;
        const isPending = !isVerified && !isDenied;

        let statusText, statusColor, statusIcon, dateText;

        if (isVerified) {
            statusText = "Verified";
            statusColor = theme.colors.secondary || "success.main";
            dateText = item.verified_at ? FormatDate(item.verified_at) : "";
        } else if (isDenied) {
            statusText = "Denied";
            statusColor = "error.main";
            dateText = item.denied_at ? FormatDate(item.denied_at) : "";
        } else {
            statusText = "Pending Verification";
            statusColor = theme.colors.text || "warning.main";
            dateText = "Waiting for owner approval";
        }

        return (
            <Box sx={{ mt: 2 }}>
                <Alert
                    severity={isVerified ? "success" : isDenied ? "error" : "warning"}
                    sx={{
                        backgroundColor: theme?.colors?.primary || 'background.paper',
                        border: `2px solid ${statusColor}`,
                        '& .MuiAlert-icon': {
                            color: statusColor
                        }
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, color: statusColor, mb: 1 }}>
                        Payment Status: {statusText}
                    </Typography>
                    {dateText && (
                        <Typography variant="body2" sx={{ color: theme.colors.text }}>
                            {isVerified || isDenied ? `${isVerified ? "Verified" : "Denied"} on ${dateText}` : dateText}
                        </Typography>
                    )}
                    {isPending && (
                        <Typography variant="body2" sx={{ color: theme.colors.text, mt: 1 }}>
                            The bill owner will check their payment app and verify this transaction.
                        </Typography>
                    )}
                </Alert>
            </Box>
        );
    };

    const modalTitle = isOwner && item.amount < 0
        ? 'Record Payment'
        : !isOwner && item.amount < 0
            ? 'Pay Bill'
            : 'Manage Bill';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme?.colors?.backgroundPrimary ?? theme?.colors?.primary,
                    color: theme?.colors?.text,
                    borderRadius: '16px',
                    maxWidth: { xs: '95vw', sm: '600px' },
                    width: { xs: '95vw', sm: '70vw' },
                }
            }}
        >
            <DialogTitle sx={{ padding: 0 }}>
                <SectionHeader
                    title={modalTitle}
                    borderRadius="16px 16px 0 0"
                    margin={0}
                />
            </DialogTitle>

            <DialogContent sx={{
                paddingTop: 0,
                paddingLeft: 0,
                paddingRight: 0,
            }}>
            <Box sx={{ px: 2, pt: '32px' }}>
                {/* Balance summary - same for Record Payment and Pay Bill (when item.amount < 0) */}
                {(isOwner && item.amount < 0) || (!isOwner && item.amount < 0) ? (
                    <>
                        <Box sx={{ p: 2, borderRadius: 1, bgcolor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)' }}>
                            <Typography variant="body2" sx={{ color: theme.colors.text, mb: 0.5 }}>
                                Balance
                            </Typography>
                            <Typography variant="h5" sx={{ color: theme.colors.error || '#f44336', fontWeight: 600 }}>
                                ${(statementsWithBalance && statementsWithBalance.length > 0
                                    ? statementsWithBalance.reduce((sum, s) => sum + s.unpaidBalance, 0)
                                    : Math.abs(item.amount || 0)
                                ).toFixed(2)}
                            </Typography>
                        </Box>
                    </>
                ) : null}

                {/* Manage Bill: owner verifying/denying a payment */}
                {isOwner && item.amount > 0 && item.id ? (
                    <Box>
                        <Box sx={{ p: 2, borderRadius: 1, bgcolor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)' }}>
                            <Typography variant="body2" sx={{ color: theme.colors.text, mb: 0.5 }}>
                                Amount Paid
                            </Typography>
                            <Typography variant="h5" sx={{ color: theme?.colors?.brandSuccess || 'success.main', fontWeight: 600 }}>
                                {formatCurrency(item.amount)}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color={theme.colors.text} gutterBottom sx={{ mt: 2, mb: 2 }}>
                            Payment from {item.user?.email || 'user'}
                        </Typography>
                        {item.verified_at ? (
                            <Alert severity="success" sx={{ backgroundColor: theme?.colors?.primary || 'background.paper', border: `2px solid ${theme.colors.secondary || "success.main"}` }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.secondary || "success.main", mb: 0.5 }}>Payment Verified</Typography>
                                <Typography variant="body2" sx={{ color: theme.colors.text }}>Verified on {FormatDate(item.verified_at)}</Typography>
                            </Alert>
                        ) : (
                            <Stack spacing={2} sx={{ mt: 2 }}>
                                <ButtonPrimary onClick={() => handleVerifyTransaction(true)} disabled={mutation.isLoading} fullWidth>
                                    {verifyTransactionMutation.isLoading ? 'Processing...' : 'Confirm'}
                                </ButtonPrimary>
                                {!item.denied_at && (
                                    <ButtonOutlined onClick={() => handleVerifyTransaction(false)} disabled={verifyTransactionMutation.isLoading} fullWidth color={theme.colors.brandError}>
                                        {verifyTransactionMutation.isLoading ? 'Processing...' : 'Deny'}
                                    </ButtonOutlined>
                                )}
                            </Stack>
                        )}
                    </Box>
                ) : null}

                {/* Manage Bill: participant viewing own payment verification status */}
                {!isOwner && item.amount > 0 ? (
                    <Box>
                        <Box sx={{ p: 2, borderRadius: 1, bgcolor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)' }}>
                            <Typography variant="body2" sx={{ color: theme.colors.text, mb: 0.5 }}>Amount Paid</Typography>
                            <Typography variant="h5" sx={{ color: theme?.colors?.brandSuccess || 'success.main', fontWeight: 600 }}>{formatCurrency(item.amount)}</Typography>
                        </Box>
                        <Typography variant="body2" color={theme.colors.text} gutterBottom sx={{ mt: 2, mb: 2 }}>Payment Verification Status</Typography>
                        {renderVerificationStatus()}
                    </Box>
                ) : null}

                {/* Record Payment: main action + Details (statement list) */}
                {isOwner && item.amount < 0 ? (
                    <Box>
                        {/*{statementsWithBalance && statementsWithBalance.length > 0 && (*/}
                        {/*    <Typography variant="caption" sx={{ color: theme.colors.text, opacity: 0.7, display: 'block', mb: 2 }}>*/}
                        {/*        {selectedStatementId ? 'Payment will be applied to the selected statement' : 'Payment will be applied to the oldest statement first'}*/}
                        {/*    </Typography>*/}
                        {/*)}*/}
                        {statementsWithBalance && statementsWithBalance.length > 0 ? (
                            <>
                                <Button
                                    fullWidth
                                    onClick={() => setDetailsExpanded((prev) => !prev)}
                                    sx={{ justifyContent: 'flex-start', color: theme.colors.text, textTransform: 'none', mb: 1 }}
                                    startIcon={detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                >
                                    Details
                                </Button>
                                <Collapse in={detailsExpanded}>
                                    <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 2 }}>
                                        <Stack spacing={1}>
                                            {statementsWithBalance.map((statement) => (
                                                <Box
                                                    key={statement.id}
                                                    onClick={() => setSelectedStatementId(statement.id)}
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 1,
                                                        border: `2px solid ${selectedStatementId === statement.id ? theme.colors.secondary : theme.colors.text}`,
                                                        cursor: 'pointer',
                                                        bgcolor: selectedStatementId === statement.id ? (theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)') : 'transparent',
                                                        '&:hover': { bgcolor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)' },
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ color: theme.colors.text, fontWeight: 500 }}>{FormatDate(statement.created_at)}</Typography>
                                                            <Typography variant="caption" sx={{ color: theme.colors.text, opacity: 0.7 }}>Total: ${statement.cost?.toFixed(2)}</Typography>
                                                        </Box>
                                                        <Typography variant="body1" sx={{ color: theme.colors.error || '#f44336', fontWeight: 600 }}>${statement.unpaidBalance.toFixed(2)} due</Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Collapse>
                            </>
                        ) : null}
                    </Box>
                ) : null}

                {/* Pay Bill: loading/error/empty message + Details (statement list); amount + Pay Venmo in footer */}
                {!isOwner && item.amount < 0 ? (
                    <Box>
                        {isLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={32} sx={{ mr: 2 }} />
                                <Typography variant="body1" color={theme.colors.text}>Loading payment options...</Typography>
                            </Box>
                        ) : isError ? (
                            <Alert severity="error" sx={{ mt: 2 }}>Failed to load payment options. Please try again.</Alert>
                        ) : !paymentOptions || !Array.isArray(paymentOptions) || paymentOptions.length === 0 ? (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">Ask the bill owner to configure payment methods</Typography>
                            </Alert>
                        ) : null}
                        {statementsWithBalance && statementsWithBalance.length > 0 ? (
                                    <>
                                        <Button
                                            fullWidth
                                            onClick={() => setDetailsExpanded((prev) => !prev)}
                                            sx={{ justifyContent: 'flex-start', color: theme.colors.text, textTransform: 'none', mt: 3, mb: 1 }}
                                            startIcon={detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        >
                                            Details
                                        </Button>
                                        <Collapse in={detailsExpanded}>
                                            <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 2 }}>
                                                <Stack spacing={1}>
                                                    {statementsWithBalance.map((statement) => (
                                                        <Box
                                                            key={statement.id}
                                                            sx={{
                                                                p: 2,
                                                                borderRadius: 1,
                                                                border: `2px solid ${theme.colors.text}`,
                                                                bgcolor: 'transparent',
                                                                '&:hover': { bgcolor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)' },
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ color: theme.colors.text, fontWeight: 500 }}>{FormatDate(statement.created_at)}</Typography>
                                                                    <Typography variant="caption" sx={{ color: theme.colors.text, opacity: 0.7 }}>Total: ${statement.cost?.toFixed(2)}</Typography>
                                                                </Box>
                                                                <Typography variant="body1" sx={{ color: theme.colors.error || '#f44336', fontWeight: 600 }}>${statement.unpaidBalance.toFixed(2)} due</Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </Collapse>
                                    </>
                                ) : null}
                    </Box>
                ) : null}
            </Box>
            </DialogContent>

            <Box sx={{
                paddingTop: 0,
                paddingBottom: 3,
                paddingLeft: 0,
                paddingRight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                px: 2,
            }}>
                {((isOwner && item.amount < 0) || (!isOwner && item.amount < 0)) && (
                    <Divider sx={{ mt: 0, mb: 1 }} />
                )}
                {isOwner && item.amount < 0 ? (
                    <>
                        <InputWithActions inputType="textfield" inputHeight="56px">
                            <InputValidatorField
                                name="amount"
                                type="number"
                                placeholder="Amount"
                                initialValue={formData.amount || ''}
                                handleChange={onChange}
                                isValidInput={(val) => {
                                    const num = parseFloat(val);
                                    return !isNaN(num) && num > 0;
                                }}
                                error={submitAttempted ? amountError : undefined}
                                helperText={submitAttempted && amountError ? "Please enter a valid amount greater than 0" : ""}
                                inputProps={{ step: 0.01 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme.colors.text,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.colors.secondary,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.colors.secondary,
                                        },
                                        color: theme.colors.text,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.colors.text,
                                    },
                                }}
                            />
                        </InputWithActions>
                        <DynamicActionButton
                            isPrimaryActive={validateAmount(formData.amount)}
                            cancelLabel="Cancel"
                            primaryContent={mutation.isLoading ? 'Processing...' : 'Record Payment'}
                            onCancelClick={handleClose}
                            onPrimaryClick={() => {
                                setSubmitAttempted(true);
                                onSubmit();
                            }}
                            disabled={mutation.isLoading}
                        />
                    </>
                ) : !isOwner && item.amount < 0 ? (
                    <>
                        <InputWithActions inputType="textfield" inputHeight="56px">
                            <InputValidatorField
                                name="amount"
                                type="number"
                                placeholder="Amount"
                                initialValue={formData.amount || ''}
                                handleChange={onChange}
                                isValidInput={(val) => {
                                    const num = parseFloat(val);
                                    return !isNaN(num) && num > 0;
                                }}
                                error={submitAttempted ? amountError : undefined}
                                helperText={submitAttempted && amountError ? "Please enter a valid amount greater than 0" : ""}
                                inputProps={{ step: 0.01 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.colors.text },
                                        '&:hover fieldset': { borderColor: theme.colors.secondary },
                                        '&.Mui-focused fieldset': { borderColor: theme.colors.secondary },
                                        color: theme.colors.text,
                                    },
                                    '& .MuiInputLabel-root': { color: theme.colors.text },
                                }}
                            />
                        </InputWithActions>
                        <DynamicActionButton
                            isPrimaryActive={
                                validateAmount(formData.amount) &&
                                paymentOptions?.some((p) => (p.provider || '').toLowerCase() === VENMO_PROVIDER && p.venmo_id)
                            }
                            cancelLabel="Cancel"
                            primaryContent="Pay with Venmo"
                            onCancelClick={handleClose}
onPrimaryClick={() => {
                                    const venmoOption = paymentOptions?.find((p) => (p.provider || '').toLowerCase() === VENMO_PROVIDER && p.venmo_id);
                                    if (venmoOption && validateAmount(formData.amount)) {
                                        const amount = parseFloat(formData.amount) || 0;
                                        setConfirmPaymentAmount(amount);
                                        handleVenmoClick(buildVenmoUrl(venmoOption.venmo_id, amount));
                                    }
                                }}
                        />
                    </>
                ) : (
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        size="large"
                        fullWidth
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                            backgroundColor: 'transparent',
                            color: theme?.colors?.text,
                            '&:hover': {
                                borderColor: theme?.colors?.border || '#ccc',
                                backgroundColor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        {item.verified_at ? 'Close' : 'Cancel'}
                    </Button>
                )}
            </Box>

            {/* Payment Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleCancelPayment}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: theme?.colors?.backgroundPrimary ?? theme?.colors?.primary,
                        color: theme?.colors?.text,
                        borderRadius: '16px',
                        maxWidth: { xs: '95vw', sm: '600px' },
                        width: { xs: '95vw', sm: '70vw' },
                    }
                }}
            >
                <DialogTitle sx={{ padding: 0 }}>
                    <SectionHeader
                        title="Confirm Payment"
                        borderRadius="16px 16px 0 0"
                        margin={0}
                    />
                </DialogTitle>
                <DialogContent sx={{ paddingTop: 0, paddingLeft: 0, paddingRight: 0 }}>
                    <Box sx={{ px: 2, pt: '32px' }}>
                        <Typography variant="body1" sx={{ color: theme.colors.text, mb: 2 }}>
                            Did you make a payment of {formatCurrency(confirmPaymentAmount ?? Math.abs(item.amount))} to {paymentOptions?.find(p => p.provider.toLowerCase() === VENMO_PROVIDER)?.venmo_id ? `@${paymentOptions.find(p => p.provider.toLowerCase() === VENMO_PROVIDER).venmo_id.replace(/^@/, '')}` : 'this person'}?
                        </Typography>
                    </Box>
                </DialogContent>
                <Box sx={{ paddingTop: 3, paddingBottom: 3, px: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                        onClick={handleConfirmPayment}
                        variant="outlined"
                        fullWidth
                        size="large"
                        disabled={confirmPaymentMutation.isLoading}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            py: 1.5,
                            borderColor: theme?.current === 'light' ? theme?.colors?.brandSecondary : (theme?.colors?.secondary || theme?.colors?.brandSecondary),
                            color: '#ffffff',
                            backgroundColor: theme?.current === 'light' ? theme?.colors?.brandSecondary : (theme?.colors?.tableBackground || theme?.colors?.background),
                            '&:hover': {
                                borderColor: theme?.current === 'light' ? theme?.colors?.brandSecondary : (theme?.colors?.secondary || theme?.colors?.brandSecondary),
                                backgroundColor: theme?.current === 'light' ? theme?.colors?.brandSecondary : (theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                            },
                        }}
                    >
                        {confirmPaymentMutation.isLoading ? 'Processing...' : 'Yes, I paid'}
                    </Button>
                    <Button
                        onClick={handleCancelPayment}
                        variant="outlined"
                        fullWidth
                        size="large"
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            py: 1.5,
                            border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                            backgroundColor: 'transparent',
                            color: theme?.colors?.text,
                            '&:hover': {
                                borderColor: theme?.colors?.border || '#ccc',
                                backgroundColor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        No, Cancel
                    </Button>
                    <Button
                        onClick={handleLaterPayment}
                        variant="outlined"
                        fullWidth
                        size="large"
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            py: 1.5,
                            border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                            backgroundColor: 'transparent',
                            color: theme?.colors?.text,
                            '&:hover': {
                                borderColor: theme?.colors?.border || '#ccc',
                                backgroundColor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        Later
                    </Button>
                </Box>
            </Dialog>
        </Dialog>
    );
};

export default TransactionModal;
